import { INSURABLE_GROUPS } from '@/features/quotes/data/group-life/insurable-groups';
import { useGroupLifeQuoteVersions } from '@/features/quotes/hooks/group-life/use-group-life-quote-versions.hook';
import type { GroupLifeTarifacaoLife } from '@/services/group-life';
import { groupLifeService } from '@/services/group-life';
import { resolveGroupLifeMonthlyPremium } from '@/features/quotes/utils/group-life/monthly-premium';
import { SearchFilterField } from '@/shared/ui';
import { Page, PageHeader } from '@/shared/ui/page';
import { formatCurrency, formatDateToBrazil } from '@/shared/utils';
import { formatRegisterNumber } from '@/shared/utils/format-register-number';
import {
    Button,
    Spinner,
    Tag,
    Text,
} from '@noorden-insurtech/alba-design-system';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { ArrowLeft, ArrowUp, ChevronRight, Home } from 'lucide-react';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
    annualToMonthly,
    monthlyLifeTotals,
} from './utils/monthly-life-premium';
import {
    usePmeTarifacao,
    type PmeTarifacaoContext,
} from './use-pme-tarifacao.hook';

type Props = {
  /** Id da análise PME — proposta (`/proposal-analyses/$proposalId/tarifacao`)
   *  ou cotação (`/analyses/$policyId/tarifacao`), conforme o `context`. */
  policyId: string;
  context: PmeTarifacaoContext;
};

/**
 * Cabeçalho da tela (Proponente/Corretor) — mesmos campos da página 1 do PDF
 * de tarifação, extraídos do bloco `parties` do endpoint de pricing.
 */
type TarifacaoHeaderInfo = {
  number: string;
  proponente: {
    legalName: string;
    /** 'Jurídica' (CNPJ) ou 'CAEPF'. */
    type: string;
    document: string;
    mainActivity?: string;
  };
  broker: {
    name: string;
    code?: string;
    susep?: string;
    branch?: string;
  };
};

/**
 * Página dedicada "Tarifação" das análises PME (proposta e cotação).
 *
 * Sub-rota da análise (botão "Tarifação" do rodapé): KPIs de prêmio, contexto
 * (estipulante/corretor/carregamento) e o grid de vidas com **infinite
 * scroll** — as coberturas/prêmios por vida vêm do endpoint agregador que
 * decodifica o `GroupPolicyCoverageData` do InsureMO. Tela somente leitura:
 * o recálculo pertence ao fluxo da proposta/cotação.
 */
export function PmeTarifacaoPage({ policyId, context }: Props) {
  const navigate = useNavigate();
  const isQuote = context === 'quote';
  const versionsQuery = useGroupLifeQuoteVersions(isQuote ? policyId : undefined);
  const currentVersion = useMemo(
    () =>
      (versionsQuery.data?.versions ?? []).find(
        version => version.isSelected?.toUpperCase() === 'Y'
      ) ??
      (versionsQuery.data?.versions ?? []).reduce<
        (NonNullable<typeof versionsQuery.data>['versions'])[number] | undefined
      >(
        (latest, version) =>
          !latest || version.quotationVersionId > latest.quotationVersionId
            ? version
            : latest,
        undefined
      ),
    [versionsQuery.data]
  );
  const currentVersionQuery = useQuery({
    queryKey: [
      'group-life',
      'quote-at-version',
      policyId,
      currentVersion?.quotationVersionId,
    ],
    queryFn: async () => {
      if (!currentVersion) throw new Error('Versão atual não encontrada');
      const response = await groupLifeService.getQuoteAtVersion(
        policyId,
        currentVersion.quotationVersionId
      );
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Erro ao carregar a versão atual');
      }
      return response.data;
    },
    enabled: isQuote && currentVersion != null,
    staleTime: 0,
  });

  // Filtro único (nome OU CPF, LIKE), com debounce. Nome → busca server-side
  // (fuzzy FullName, varre a apólice inteira); CPF → filtra client-side as
  // vidas já carregadas (o InsureMO não indexa CPF), pelo campo `cpf` da vida.
  const [searchInput, setSearchInput] = useState('');
  const search = useDebouncedValue(searchInput.trim(), 400);
  const searchDigits = search.replace(/\D/g, '');
  const isCpfSearch =
    searchDigits.length >= 3 && searchDigits.length >= search.length - 3;
  const serverSearch = !isCpfSearch && search ? search : undefined;

  const {
    data,
    isLoading,
    isError,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isPlaceholderData,
  } = usePmeTarifacao(policyId, context, serverSearch);

  const pages = data?.pages ?? [];
  const loadedLives = useMemo(() => pages.flatMap(page => page.data), [pages]);
  // Filtro por CPF é aplicado sobre as vidas já carregadas.
  const lives = useMemo(
    () =>
      isCpfSearch
        ? loadedLives.filter(life =>
            life.cpf.replace(/\D/g, '').includes(searchDigits)
          )
        : loadedLives,
    [isCpfSearch, loadedLives, searchDigits]
  );
  const firstPage = pages[0];
  const notCalculated = pages.some(page => page.notCalculated);
  const notDetached = firstPage ? !firstPage.detached : false;
  // "Vidas seguradas" e prêmio dos KPIs = nível apólice (não muda com filtro).
  const totalLives = firstPage?.livesTotal ?? firstPage?.meta.total ?? 0;
  // Contagem do grid respeita a busca (server total p/ nome; loaded p/ CPF).
  const gridCount = isCpfSearch ? lives.length : (firstPage?.meta.total ?? 0);
  const hasSearch = search.length > 0;
  // Progresso de carregamento (infinite scroll) da query atual.
  const loadedCount = loadedLives.length;
  const queryTotal = firstPage?.meta.total ?? 0;

  // Cabeçalho: bloco `parties` que o endpoint de pricing extrai do snapshot
  // real da apólice.
  const analysis = useMemo<TarifacaoHeaderInfo | null>(() => {
    const parties = firstPage?.parties;
    if (!parties) return null;
    return {
      number: parties.proposalNumber ?? '',
      proponente: {
        legalName: parties.proponente.legalName,
        type: parties.proponente.type,
        document: parties.proponente.document,
        mainActivity: parties.proponente.mainActivity,
      },
      broker: {
        name: parties.broker?.name ?? '',
        code: parties.broker?.code,
        susep: parties.broker?.susep,
        branch: parties.broker?.branch,
      },
    };
  }, [firstPage]);

  // Carregamento: percentuais reais que o endpoint extrai do snapshot
  // (BrokerageRate/CommissionRate/DiscountLoadingList). `null` = "—".
  const carregamento = useMemo(() => {
    const snapshot = currentVersionQuery.data?.commercialization;
    const real = firstPage?.commercialization;
    if (snapshot) {
      const pct = (value?: string) => (value ? `${value}%` : '—');
      return {
        brokerage: pct(snapshot.brokeragePercent),
        agency: pct(snapshot.agencingPercent),
        surcharge: pct(snapshot.surchargePercent),
        discount: pct(snapshot.actuarialDiscountPercent),
      };
    }
    if (!real) return null;
    const pct = (value: number | null) =>
      value == null
        ? '—'
        : `${value.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%`;
    return {
      brokerage: pct(real.brokeragePercent),
      agency: pct(real.agencingPercent ?? real.agencyPercent),
      surcharge: pct(real.surchargePercent),
      discount: pct(real.actuarialDiscountPercent),
    };
  }, [currentVersionQuery.data, firstPage]);

  // Prêmios dos KPIs: bloco `premium` (nível apólice, do snapshot) — não muda
  // com filtro/paginação.
  const kpis = useMemo(() => {
    // Fonte única do prêmio mensal (`calculation.monthly`) — o mesmo número
    // da proposta, do resumo da cotação e dos PDFs.
    const monthly = resolveGroupLifeMonthlyPremium(
      currentVersionQuery.data?.calculation
    );
    if (monthly) {
      return { due: monthly.total, net: monthly.beforeIof, iof: monthly.iof };
    }
    const premium = firstPage?.premium;
    if (!premium) return null;
    // Bloco `premium` é ANUAL — converte para mensal como o resto da tela.
    return {
      due: annualToMonthly(premium.total ?? 0),
      net: annualToMonthly(premium.net ?? 0),
      iof: annualToMonthly(premium.iof ?? 0),
    };
  }, [currentVersionQuery.data, firstPage]);

  // Sentinel do infinite scroll: aproxima do fim → busca a próxima página.
  const sentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      entries => {
        // `isPlaceholderData` = dados da busca anterior em exibição enquanto a
        // nova carrega — não paginar sobre eles.
        if (
          entries[0]?.isIntersecting &&
          hasNextPage &&
          !isFetchingNextPage &&
          !isPlaceholderData
        ) {
          void fetchNextPage();
        }
      },
      { rootMargin: '320px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, isPlaceholderData]);

  // "Voltar ao topo": aparece quando o topo da página sai da viewport.
  const topRef = useRef<HTMLDivElement>(null);
  const [showBackToTop, setShowBackToTop] = useState(false);
  useEffect(() => {
    const top = topRef.current;
    if (!top) return;
    const observer = new IntersectionObserver(entries => {
      setShowBackToTop(!(entries[0]?.isIntersecting ?? true));
    });
    observer.observe(top);
    return () => observer.disconnect();
  }, []);

  const goBack = () =>
    isQuote
      ? void navigate({
          to: '/analyses/$policyId',
          params: { policyId },
        })
      : void navigate({
          to: '/proposal-analyses/$proposalId',
          params: { proposalId: policyId },
        });

  return (
    <Page.Root>
      <div ref={topRef} />
      <Page.Breadcrumb
        items={[
          {
            label: 'Pagina Inicial',
            href: '/dashboard',
            iconLeft: <Home className='h-4 w-4' />,
          },
          { label: 'Subscrição' },
          isQuote
            ? { label: 'Análise de Cotação', href: '/analyses' }
            : { label: 'Propostas em Análise', href: '/proposal-analyses' },
          {
            label: 'Análise PME',
            href: isQuote
              ? `/analyses/${policyId}`
              : `/proposal-analyses/${policyId}`,
          },
          { label: 'Tarifação', active: true },
        ]}
      />

      <button
        type='button'
        onClick={goBack}
        className='flex w-fit cursor-pointer items-center gap-1.5 font-montserrat text-sm font-semibold text-alba-gray-600 hover:text-alba-black-500'
      >
        <ArrowLeft className='h-4 w-4' />
        Voltar
      </button>

      <PageHeader.Root>
        <PageHeader.Main>
          <PageHeader.TitleGroup>
            <PageHeader.Title>
              Tarifação
              {analysis?.number
                ? ` - ${isQuote ? 'Cotação' : 'Proposta'} Nº ${analysis.number}`
                : ''}
            </PageHeader.Title>
            <PageHeader.Subtitle>
              Prêmios e coberturas aplicados a cada vida da{' '}
              {isQuote ? 'cotação' : 'proposta'}
            </PageHeader.Subtitle>
          </PageHeader.TitleGroup>
          <PageHeader.Actions>
            <Tag.Root variant='outline' colorScheme='yellow' size='lg'>
              <Tag.Label>Em Análise</Tag.Label>
            </Tag.Root>
          </PageHeader.Actions>
        </PageHeader.Main>
      </PageHeader.Root>

      <Page.Content className='flex flex-col gap-4 py-4'>
        {/* ─── KPIs ─── */}
        <div className='grid grid-cols-2 gap-3 lg:grid-cols-5'>
          <KpiCard
            hero
            label='Prêmio total'
            value={kpis ? formatCurrency(kpis.due) : '—'}
            hint='cobrança mensal'
          />
          <KpiCard
            label='Prêmio líquido'
            value={kpis ? formatCurrency(kpis.net) : '—'}
          />
          <KpiCard label='IOF' value={kpis ? formatCurrency(kpis.iof) : '—'} />
          <KpiCard
            label='Vidas seguradas'
            value={totalLives > 0 ? String(totalLives) : '—'}
          />
          <KpiCard
            label='Prêmio médio / vida'
            value={
              kpis && totalLives > 0
                ? formatCurrency(kpis.due / totalLives)
                : '—'
            }
          />
        </div>

        {/* ─── Contexto (estipulante / corretor / carregamento) ─── */}
        {analysis && (
          <div className='grid grid-cols-1 gap-3 lg:grid-cols-2'>
            <section className='rounded-2xl bg-white p-4 shadow-[0px_4px_24px_rgba(15,22,30,0.08)] sm:p-6'>
              <SectionLabel>Proponente</SectionLabel>
              <div className='mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2'>
                <InfoPair
                  className='sm:col-span-2'
                  label='Razão social'
                  value={analysis.proponente.legalName}
                />
                <InfoPair label='Tipo' value={analysis.proponente.type} />
                <InfoPair
                  label='CNPJ'
                  value={formatRegisterNumber(analysis.proponente.document)}
                />
                <InfoPair
                  className='sm:col-span-2'
                  label='Atividade principal'
                  value={analysis.proponente.mainActivity ?? '—'}
                />
              </div>
            </section>
            <section className='rounded-2xl bg-white p-4 shadow-[0px_4px_24px_rgba(15,22,30,0.08)] sm:p-6'>
              <SectionLabel>Corretor</SectionLabel>
              <div className='mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3'>
                <InfoPair
                  className='sm:col-span-3'
                  label='Nome'
                  value={analysis.broker.name}
                />
                <InfoPair label='Código' value={analysis.broker.code ?? '—'} />
                <InfoPair label='SUSEP' value={analysis.broker.susep ?? '—'} />
                <InfoPair
                  label='Filial'
                  value={analysis.broker.branch ?? '—'}
                />
              </div>
            </section>
          </div>
        )}

        {carregamento && (
          <section className='rounded-2xl bg-white p-4 shadow-[0px_4px_24px_rgba(15,22,30,0.08)] sm:p-6'>
            <SectionLabel>Carregamento</SectionLabel>
            <div className='mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4'>
              <InfoPair
                label='Percentual da corretagem'
                value={carregamento.brokerage}
              />
              <InfoPair
                label='Percentual do agenciamento'
                value={carregamento.agency}
              />
              <InfoPair label='Agravo' value={carregamento.surcharge} />
              <InfoPair
                label='Desconto atuarial'
                value={carregamento.discount}
              />
            </div>
          </section>
        )}

        {/* ─── Avisos ─── */}
        {notCalculated && (
          <Banner tone='warning'>
            Proposta ainda não calculada — os prêmios serão exibidos após o
            cálculo no fluxo da proposta.
          </Banner>
        )}
        {notDetached && (
          <Banner tone='info'>
            Esta apólice não possui vidas destacadas — as coberturas são as do
            grupo segurável, consultáveis na própria análise.
          </Banner>
        )}

        {/* ─── Grid de vidas (infinite scroll) ─── */}
        <section className='overflow-hidden rounded-2xl bg-white shadow-[0px_4px_24px_rgba(15,22,30,0.08)]'>
          <div className='flex flex-wrap items-center gap-3 border-b border-alba-gray-200 px-4 py-4 sm:px-6'>
            <Text className='font-montserrat text-base text-alba-black-500'>
              Tarifação por vida
            </Text>
            {gridCount > 0 && (
              <span className='rounded-full bg-alba-gray-100 px-3 py-0.5 font-montserrat text-xs font-bold text-alba-black-500'>
                {gridCount} {gridCount === 1 ? 'vida' : 'vidas'}
              </span>
            )}
            {!isCpfSearch && totalLives > 0 && lives.length < gridCount && (
              <Text className='font-montserrat text-xs text-alba-gray-600'>
                {lives.length} carregadas
              </Text>
            )}
            {isPlaceholderData && (
              <span className='flex items-center gap-2'>
                <Spinner size='sm' />
                <Text className='font-montserrat text-xs text-alba-gray-600'>
                  Buscando…
                </Text>
              </span>
            )}
            <div className='ml-auto w-full sm:w-72'>
              <SearchFilterField
                value={searchInput}
                onChange={value => setSearchInput(String(value ?? ''))}
                placeholder='Buscar por nome ou CPF…'
              />
            </div>
          </div>

          {isError ? (
            <div className='flex flex-col items-center gap-3 px-6 py-10 text-center'>
              <Text className='font-montserrat text-sm text-alba-error-red-500'>
                Erro ao carregar a tarifação. Tente novamente.
              </Text>
              <Button
                variant='secondary'
                size='md'
                onClick={() => void refetch()}
              >
                Tentar novamente
              </Button>
            </div>
          ) : isLoading ? (
            <LivesSkeleton rows={6} withHeader />
          ) : loadedLives.length === 0 ? (
            <div className='px-6 py-10 text-center'>
              <Text className='font-montserrat text-sm text-alba-gray-600'>
                {hasSearch
                  ? 'Nenhuma vida encontrada para essa busca.'
                  : 'Nenhuma vida encontrada para esta proposta.'}
              </Text>
            </div>
          ) : (
            <div
              className={`p-4 sm:p-6 ${
                isPlaceholderData
                  ? 'pointer-events-none opacity-60 transition-opacity'
                  : ''
              }`}
              aria-busy={isPlaceholderData}
            >
              <div className='overflow-x-auto'>
                <div className='min-w-[1080px] overflow-hidden rounded-3xl bg-white shadow-sm'>
                  <LivesGridHeader />
                  {lives.map(life => (
                    <LifeRow key={life.policyElementId} life={life} />
                  ))}
                  {/* CPF filtrado zerou nas vidas carregadas — orienta carregar mais. */}
                  {lives.length === 0 && (
                    <div className='border-b border-alba-gray-300 px-4 py-6 text-center'>
                      <Text className='font-montserrat text-sm text-alba-gray-600'>
                        Nenhuma das {loadedCount} vidas carregadas tem esse CPF.
                        {hasNextPage ? ' Role para carregar mais vidas.' : ''}
                      </Text>
                    </div>
                  )}
                  {isFetchingNextPage && <LivesSkeleton rows={3} />}
                  <div ref={sentinelRef} aria-hidden='true' />
                  <div className='flex items-center justify-center gap-2 px-4 py-3'>
                    {isFetchingNextPage ? (
                      <>
                        <Spinner size='sm' />
                        <Text className='font-montserrat text-xs text-alba-gray-600'>
                          Carregando mais vidas… {loadedCount} de {queryTotal}
                        </Text>
                      </>
                    ) : hasNextPage ? (
                      <Text className='font-montserrat text-xs text-alba-gray-600'>
                        Role para carregar mais vidas — {loadedCount} de{' '}
                        {queryTotal}
                      </Text>
                    ) : (
                      <Text className='font-montserrat text-xs text-alba-gray-600'>
                        {isCpfSearch
                          ? `${loadedCount} vidas carregadas`
                          : `Todas as ${queryTotal} vidas foram carregadas`}
                      </Text>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </section>
      </Page.Content>

      {showBackToTop && (
        <button
          type='button'
          onClick={() => topRef.current?.scrollIntoView({ behavior: 'smooth' })}
          className='fixed bottom-8 right-8 z-30 flex items-center gap-2 rounded-full bg-[#2C285F] px-5 py-3 font-montserrat text-xs font-bold text-white shadow-[0px_8px_24px_rgba(24,22,61,0.35)] hover:opacity-90'
        >
          <ArrowUp className='h-4 w-4' />
          Voltar ao topo
        </button>
      )}
    </Page.Root>
  );
}

/** Valor com debounce — evita disparar a busca a cada tecla. */
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

// ─── Grid de vidas ───────────────────────────────────────────────────────────

const GRID_COLS =
  'grid grid-cols-[28px_2fr_1.3fr_0.9fr_1.3fr_1.1fr_1.1fr_0.8fr_1.1fr] items-center gap-3 px-4';

/** Header idêntico ao do DataTable do design system: thead `bg-alba-gray-200`
 *  + `border-b-2 border-alba-gray-300`, células `p-4 uppercase font-montserrat
 *  text-xs font-bold text-alba-black-500`. */
function LivesGridHeader() {
  const cell =
    'font-montserrat text-xs font-bold uppercase text-alba-black-500';
  return (
    <div
      className={`${GRID_COLS} border-b-2 border-alba-gray-300 bg-alba-gray-200 py-4`}
    >
      <span />
      <Text className={cell}>Nome</Text>
      <Text className={cell}>Grupo</Text>
      <Text className={cell}>Sexo</Text>
      <Text className={cell}>Nascimento</Text>
      <Text className={`${cell} text-right`}>Capital</Text>
      <Text className={`${cell} text-right`}>Prêmio líquido</Text>
      <Text className={`${cell} text-right`}>IOF</Text>
      <Text className={`${cell} text-right`}>Prêmio total</Text>
    </div>
  );
}

function LifeRow({ life }: { life: GroupLifeTarifacaoLife }) {
  const [expanded, setExpanded] = useState(false);

  const genderLabel =
    life.gender === 'M' ? 'Masculino' : life.gender === 'F' ? 'Feminino' : '—';
  const groupLabel =
    INSURABLE_GROUPS.find(group => group.value === life.insurableGroup)
      ?.label ??
    life.insurableGroup ??
    '—';
  const birth = life.birthDate ? formatDateToBrazil(life.birthDate) : '—';
  const age = life.birthDate
    ? Math.floor(
        DateTime.now().diff(DateTime.fromISO(life.birthDate), 'years').years
      )
    : null;

  const monthly = monthlyLifeTotals(life);

  return (
    <>
      <button
        type='button'
        onClick={() => setExpanded(previous => !previous)}
        aria-expanded={expanded}
        className={`${GRID_COLS} w-full cursor-pointer border-b border-alba-gray-300 bg-white py-4 text-left last:border-b-0 hover:bg-alba-gray-100/50`}
      >
        <ChevronRight
          className={`h-4 w-4 transition-transform ${
            expanded
              ? 'rotate-90 text-alba-success-green-500'
              : 'text-alba-gray-600'
          }`}
        />
        <Text className='truncate font-montserrat text-sm font-bold text-alba-black-500'>
          {life.name || '—'}
        </Text>
        <span className='w-fit rounded bg-[#EEEDF5] px-2 py-0.5 font-montserrat text-[10px] font-bold uppercase text-[#2C2864]'>
          {groupLabel}
        </span>
        <Text className='font-montserrat text-sm text-alba-gray-600'>
          {genderLabel}
        </Text>
        <Text className='font-montserrat text-sm text-alba-gray-600'>
          {birth}
          {age != null ? ` · ${age} anos` : ''}
        </Text>
        <Text className='text-right font-montserrat text-sm tabular-nums text-alba-gray-600'>
          {life.capital || '—'}
        </Text>
        <Text className='text-right font-montserrat text-sm tabular-nums text-alba-gray-600'>
          {formatCurrency(monthly.net)}
        </Text>
        <Text className='text-right font-montserrat text-sm tabular-nums text-alba-gray-600'>
          {monthly.iof > 0 ? formatCurrency(monthly.iof) : '—'}
        </Text>
        <Text className='text-right font-montserrat text-sm font-bold tabular-nums text-alba-black-500'>
          {formatCurrency(monthly.due)}
        </Text>
      </button>

      {expanded && (
        <div className='border-b border-alba-gray-300 bg-alba-gray-100/40 px-4 py-4'>
          {life.hasOwnCoverages ? (
            <CoveragesTable life={life} />
          ) : (
            <Text className='font-montserrat text-xs text-alba-gray-600'>
              Vida sem coberturas próprias — herda as coberturas do grupo
              segurável (sem override individual).
            </Text>
          )}
        </div>
      )}
    </>
  );
}

const COVERAGE_COLS =
  'grid grid-cols-[1.6fr_1fr_1.9fr_1fr_1fr_1fr_1fr_0.8fr_1fr] items-center gap-3 px-4';

function CoveragesTable({ life }: { life: GroupLifeTarifacaoLife }) {
  const header =
    'font-montserrat text-xs font-bold uppercase text-alba-black-500';
  const cell = 'font-montserrat text-xs tabular-nums text-alba-gray-600';
  // Prêmios do InsureMO são anuais; a tela exibe o valor mensal (÷ 12).
  const money = (value: number | null) =>
    value != null ? formatCurrency(annualToMonthly(value)) : '—';
  const monthlyTotal = monthlyLifeTotals(life).due;
  const capital = (value: number | null) =>
    value != null && value !== 0 ? formatCurrency(value) : '—';
  // Taxas exibidas com a precisão da origem (não são moeda).
  const rate = (value: number | null) =>
    value != null
      ? value.toLocaleString('pt-BR', { maximumFractionDigits: 8 })
      : '—';

  return (
    <div className='ml-7'>
      <Text className='mb-2 font-montserrat text-sm font-bold text-alba-black-500'>
        Garantias
      </Text>
      <div className='overflow-hidden rounded-xl bg-white shadow-sm'>
        <div
          className={`${COVERAGE_COLS} border-b-2 border-alba-gray-300 bg-alba-gray-200 py-2.5`}
        >
          <Text className={header}>Garantia</Text>
          <Text className={`${header} text-right`}>Capital</Text>
          <Text className={`${header} text-center`}>Fatores / Taxa</Text>
          <Text className={`${header} text-right`}>Taxa net</Text>
          <Text className={`${header} text-right`}>Taxa comercial</Text>
          <Text className={`${header} text-right`}>Prêmio puro</Text>
          <Text className={`${header} text-right`}>Prêmio líquido</Text>
          <Text className={`${header} text-right`}>IOF</Text>
          <Text className={`${header} text-right`}>Prêmio total</Text>
        </div>
        {life.coverages.map(coverage => (
          <div
            key={`${coverage.code}-${coverage.name}`}
            className={`${COVERAGE_COLS} border-b border-alba-gray-300 py-2.5`}
          >
            <Text className='font-montserrat text-xs font-medium text-alba-black-500'>
              {coverage.name}
            </Text>
            <Text className={`${cell} text-right`}>
              {capital(coverage.sumInsured)}
            </Text>
            {/* FATORES/TAXA: mini-tabela (composição à esquerda, fator à
                direita), 1 linha sexo/idade + 1 opcional de desconto CNAE. */}
            {coverage.factors.length > 0 ? (
              <div className='flex flex-col'>
                {coverage.factors.map((factor, index) => (
                  <div
                    key={`${factor.label}-${index}`}
                    className={`flex items-center justify-between gap-2 py-1 ${
                      index > 0 ? 'border-t border-alba-gray-200' : ''
                    }`}
                  >
                    <Text className={`${cell} uppercase`}>{factor.label}</Text>
                    <Text className={`${cell} text-right`}>
                      {rate(factor.value)}
                    </Text>
                  </div>
                ))}
              </div>
            ) : (
              <Text className={`${cell} text-center`}>—</Text>
            )}
            <Text className={`${cell} text-right`}>
              {rate(coverage.netRate)}
            </Text>
            <Text className={`${cell} text-right`}>
              {rate(coverage.commercialRate)}
            </Text>
            <Text className={`${cell} text-right`}>
              {money(coverage.purePremium)}
            </Text>
            <Text className={`${cell} text-right`}>
              {money(coverage.netPremium)}
            </Text>
            <Text className={`${cell} text-right`}>{money(coverage.iof)}</Text>
            <Text className='text-right font-montserrat text-xs font-bold tabular-nums text-alba-black-500'>
              {money(coverage.duePremium)}
            </Text>
          </div>
        ))}
        <div className={`${COVERAGE_COLS} bg-alba-gray-200 py-2.5`}>
          <Text className='font-montserrat text-xs font-bold text-alba-black-500'>
            Total da vida
          </Text>
          <span className='col-span-7' />
          <Text className='text-right font-montserrat text-xs font-bold tabular-nums text-alba-black-500'>
            {formatCurrency(monthlyTotal)}
          </Text>
        </div>
      </div>
    </div>
  );
}

function LivesSkeleton({
  rows,
  withHeader = false,
}: {
  rows: number;
  withHeader?: boolean;
}) {
  return (
    <div>
      {withHeader && (
        <div className='border-b-2 border-alba-gray-300 bg-alba-gray-200 px-4 py-4'>
          <div className='h-3 w-1/3 animate-pulse rounded bg-alba-gray-300' />
        </div>
      )}
      {Array.from({ length: rows }).map((_, index) => (
        <div
          key={index}
          className={`${GRID_COLS} border-b border-alba-gray-300 py-4 last:border-b-0`}
        >
          <span />
          {Array.from({ length: 8 }).map((_, cellIndex) => (
            <div
              key={cellIndex}
              className='h-3 animate-pulse rounded bg-alba-gray-200'
              style={{ width: `${55 + ((index + cellIndex) % 4) * 12}%` }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

// ─── Blocos auxiliares ───────────────────────────────────────────────────────

function KpiCard({
  label,
  value,
  hint,
  hero = false,
}: {
  label: string;
  value: string;
  hint?: string;
  hero?: boolean;
}) {
  return (
    <div
      className={
        hero
          ? 'rounded-2xl bg-linear-to-br from-[#2C285F] to-[#18163D] px-4 py-4'
          : 'rounded-2xl bg-white px-4 py-4 shadow-[0px_4px_24px_rgba(15,22,30,0.08)]'
      }
    >
      <Text
        className={`font-montserrat text-[10px] font-bold uppercase tracking-wide ${
          hero ? 'text-white/70' : 'text-alba-gray-600'
        }`}
      >
        {label}
      </Text>
      <Text
        className={`mt-1 font-montserrat text-xl font-bold tabular-nums ${
          hero ? 'text-white' : 'text-[#2C2864]'
        }`}
      >
        {value}
      </Text>
      {hint && (
        <Text
          className={`mt-0.5 font-montserrat text-[11px] ${
            hero ? 'text-white/50' : 'text-alba-gray-600'
          }`}
        >
          {hint}
        </Text>
      )}
    </div>
  );
}

/** Título de seção no padrão das demais telas (ex.: "Versões",
 *  "Comercialização" na análise — `pme-analysis-sections.component.tsx`). */
function SectionLabel({ children }: { children: ReactNode }) {
  return <Text className='text-base text-alba-black-500'>{children}</Text>;
}

/** Par rótulo/valor no padrão da seção Comercialização da análise:
 *  rótulo `text-sm font-bold` preto, valor `text-sm` cinza. */
function InfoPair({
  label,
  value,
  className = '',
}: {
  label: string;
  value: string;
  className?: string;
}) {
  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      <Text className='font-montserrat text-sm font-bold text-alba-black-500'>
        {label}
      </Text>
      <Text className='font-montserrat text-sm text-alba-gray-600'>
        {value || '—'}
      </Text>
    </div>
  );
}

function Banner({
  tone,
  children,
}: {
  tone: 'warning' | 'info';
  children: ReactNode;
}) {
  const styles =
    tone === 'warning'
      ? 'border-[#F0D9A8] bg-[#FEF6E7] text-[#B7791F]'
      : 'border-alba-gray-200 bg-alba-gray-100 text-alba-gray-600';
  return (
    <div
      className={`rounded-xl border px-4 py-3 font-montserrat text-sm ${styles}`}
    >
      {children}
    </div>
  );
}
